import React from "react";

export const Sidebar = ({
  structure,
  active,
  onSelect,
  openMenus,
  onToggle,
  collapsed = false,
  onCollapseToggle,
}) => {
  /**
   * Paste your Firebase Storage HTTPS URLs below.
   * Example format:
   * https://firebasestorage.googleapis.com/v0/b/YOUR_BUCKET/o/icons%2Fproduction.png?alt=media&token=XYZ
   */
  const iconUrlMap = {
    "Production Tracker":
      "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/ChatGPT%20Image%20Sep%2014%2C%202025%2C%2010_07_20%20PM.png?alt=media&token=8bcac8b2-de4a-4e19-83c6-f3beb8d82362",
    "Org Chart":
      "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/ChatGPT%20Image%20Sep%2014%2C%202025%2C%2010_01_02%20PM.png?alt=media&token=abf164e2-1f8e-48c1-8ede-a987978fb0e0",
    "4x4 Tracker":
      "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/4x4logo.png?alt=media&token=4867ff8e-efdd-4933-805d-01f193837337",

    // ⬇️ Add/replace your Prospect Tracker icon URL here
    "Prospect Tracker":
      "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/ChatGPT%20Image%20Sep%2014%2C%202025%2C%2010_36_17%20PM.png?alt=media&token=18176c40-6bce-476f-86b3-d12c119f1758",
  };

  /** Emoji fallback for everything else */
  const iconEmojiMap = {
    // Top level
    Home: "🏠",
    "Insight Center": "💡",
    "Training Center": "🎯",
    Licensing: "🪪",
    "10 Systematic Tools": "🛠️",
    "Big Event": "🎟️",
    "Training Schedule": "📆",
    Calendar: "🗓️",
    "File Vault": "📁",
    "Help Desk": "🆘",

    // Group
    "My Team": "👥",

    // My Team children (overridden by image when provided)
    "Org Chart": "🗂️",
    "Prospect Tracker": "🧲",
    "4x4 Tracker": "🧱",
    "Licensing Tracker": "🪪",
    "Production Tracker": "🏭",
    "Associate Tracker": "🧑‍🤝‍🧑",
  };

  const isActive = (val) => active === val;

  const groupHasActive = (items = []) =>
    items?.some((child) =>
      typeof child === "string" ? child === active : child?.value === active
    );

  /** Image-or-emoji icon that falls back if the image fails */
  const Icon = ({ label, explicit }) => {
    // Allow a per-item override like: { icon: { type: 'img', src: 'https://...' } }
    if (
      explicit &&
      typeof explicit === "object" &&
      explicit.type === "img" &&
      explicit.src
    ) {
      return (
        <ImgOrEmoji
          label={label}
          src={explicit.src}
          emojiMap={iconEmojiMap}
          collapsed={collapsed}
        />
      );
    }

    const src = iconUrlMap[label] || null;
    return (
      <ImgOrEmoji
        label={label}
        src={src}
        emojiMap={iconEmojiMap}
        collapsed={collapsed}
      />
    );
  };

  /** Renders an <img> if src exists and loads; otherwise emoji */
  const ImgOrEmoji = ({ label, src, emojiMap, collapsed }) => {
    const [broken, setBroken] = React.useState(false);
    const emojiKey = emojiMap[label] || "•";

    if (src && !broken) {
      return (
        <span className="nav-icon" aria-hidden>
          <img
            src={src}
            alt=""
            className="nav-icon-img"
            loading="eager"
            referrerPolicy="no-referrer"
            onError={() => setBroken(true)}
          />
        </span>
      );
    }
    return (
      <span className="nav-icon" aria-hidden>
        {emojiKey}
      </span>
    );
  };

  /** Child renderers (strings or rich objects) */
  const renderChild = (child) => {
    if (typeof child === "string") {
      return (
        <button
          key={child}
          className={`side-btn child ${isActive(child) ? "active" : ""}`}
          onClick={() => onSelect(child)}
          title={collapsed ? child : undefined}
        >
          <Icon label={child} />
          <span className="nav-label">{child}</span>
        </button>
      );
    }

    const type = child.type || "link";
    if (type === "link") {
      const label = child.label || child.value || "Item";
      const value = child.value || label;
      return (
        <button
          key={value}
          className={`side-btn child ${isActive(value) ? "active" : ""}`}
          onClick={() => onSelect(value)}
          title={collapsed ? label : undefined}
        >
          <Icon label={label} explicit={child.icon} />
          <span className="nav-label">{label}</span>
        </button>
      );
    }

    return null;
  };

  /** Top-level item renderer (string or dropdown group) */
  const renderItem = (item) => {
    if (typeof item === "string") {
      return (
        <button
          key={item}
          className={`side-btn ${isActive(item) ? "active" : ""}`}
          onClick={() => onSelect(item)}
          title={collapsed ? item : undefined}
        >
          <Icon label={item} />
          <span className="nav-label">{item}</span>
        </button>
      );
    }

    const label = item.label || "Menu";
    const key = item.key || label.toUpperCase().replace(/\s+/g, "");
    const open = openMenus?.[key];

    return (
      <div key={key} className="side-dropdown">
        <button
          className={`side-btn drop-head ${open ? "open" : ""} ${
            groupHasActive(item.items) ? "active" : ""
          }`}
          onClick={() => onToggle(key)}
          title={collapsed ? label : undefined}
          aria-expanded={!!open}
          aria-controls={`drop-${key}`}
        >
          <Icon label={label} explicit={item.icon} />
          <span className="nav-label">{label}</span>
          {!collapsed && <span className="chev">{open ? "▾" : "▸"}</span>}
        </button>

        {open && item.items?.length > 0 && (
          <div
            id={`drop-${key}`}
            className={`drop-items ${collapsed ? "hidden" : ""}`}
          >
            {item.items.map(renderChild)}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className={`crm-sidebar ${collapsed ? "collapsed" : ""}`}>
      <div className="side-topbar">
        {!collapsed && <div className="side-title">Menu</div>}
        <button
          className="collapse-btn"
          onClick={onCollapseToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? "»" : "«"}
        </button>
      </div>

      <nav className="side-menu">{structure.map(renderItem)}</nav>
    </aside>
  );
};

export default Sidebar;
